import { faCode } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { InputRule } from "prosemirror-inputrules";
import { MarkType } from "prosemirror-model";
import { EditorView } from "prosemirror-view";

import { escapeMark, toggleMark } from "../commands";
import { type KeysArgs, MarkExtension } from "../extension";
import { markActive } from "../utils";

const codeRegex = /(?:^|[^a-zA-Z\d`])`([^\s`](?:[^\v`]|`\s)*[^\s`]|[^\s`])`([^\s`]|$)$/g;

export default class CodeMark extends MarkExtension {
	get name() {
		return "code";
	}

	get mark() {
		return {
			parseDOM: [
				{ tag: "code" },
				{
					tag: "span",
					getAttrs: (node: HTMLElement) => {
						if (!node.classList.contains("prose-code")) return false;
						return {};
					},
				},
			],
			inclusive: true,
			toDOM() {
				return ["code", { class: "prose-code" }, 0];
			},
		};
	}

	keys({ $type }: KeysArgs) {
		return {
			"Mod-Shift-c": toggleMark($type as MarkType, {}),
			ArrowRight: escapeMark($type as MarkType),
		};
	}

	inputRules() {
		return [
			new InputRule(codeRegex, (state, match, start, end) => {
				const codeMark = state.schema.mark(this.name);

				// Don't apply the mark if a code mark is active.
				// This makes it so you can use the backtick in code
				if (markActive(codeMark)(state)) return null;

				// The regex may match a non-word character ahead of the opening backtick to assure that there
				// 	is not a second backtick. This character then appears in match[0] though it's unneeded
				const prefixSymbolCompensation = !match[0].startsWith("`") ? 1 : 0;

				return state.tr
					.insertText("", start + prefixSymbolCompensation, start + 1 + prefixSymbolCompensation)
					.insertText("", end, end)
					.addMark(start + prefixSymbolCompensation, end - 1, codeMark)
					.removeStoredMark(codeMark);
			}),
		];
	}

	menuEntry(_view: EditorView) {
		return {
			icon: faCode,
			title: "Code",
			run: () => toggleMark(this.$type, {}),
			section: "formatting" as const,
			group: "basic",
			priority: 1,
			keyboardShortcut: this.cmdPlus("Shift + C"),
			hideFromTeamsSidebar: true,
		};
	}
}
